import { useState } from "react";
import { ExternalLink, SquarePen, Copy, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Message } from "@/contexts/AIAssistantContext";
import type { AIStatus } from "@/lib/ai-chat/types";
import { Mascot } from "@/components/mascot";

// The status type lives with the AI-chat types now: Aurora's chat renders the
// same three states, and importing it from `ai-assistant/` would have left
// `ai/AIChat.tsx` depending on the widget directory.
export type { AIStatus };

interface ChatMessageProps {
  message: Message;
  onEdit?: () => void;
  aiStatus?: AIStatus;
}

function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function ChatMessage({ message, onEdit, aiStatus }: ChatMessageProps) {
  const isUser = message.role === "user";
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // silently fail
    }
  };

  const iconBtnClass =
    "text-muted-foreground/50 hover:text-muted-foreground rounded-md p-1 hover:bg-muted-foreground/10 transition-all";

  if (isUser) {
    return (
      <div className="flex flex-col items-end gap-0.5">
        <div className="max-w-[85%] text-sm leading-relaxed bg-primary-strong text-primary-foreground rounded-2xl rounded-br-sm px-3.5 py-2.5">
          <p className="whitespace-pre-wrap break-words">{message.content}</p>
        </div>
        <div className="max-w-[85%] flex items-center gap-1.5 px-1">
          <span className="text-xs text-muted-foreground/50">
            {formatTime(message.timestamp)}
          </span>
          <button onClick={handleCopy} className={iconBtnClass} title="Copy message">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </button>
          {onEdit && (
            <button onClick={onEdit} className={iconBtnClass} title="Edit and resend">
              <SquarePen className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  const mascotMood = aiStatus === "thinking"
    ? "thinking"
    : aiStatus === "generating"
    ? "hard"
    : "idle";

  return (
    <div className="flex justify-start gap-2.5">
      <Mascot mood={mascotMood} size="xs" />
      <div className="max-w-[85%] text-sm leading-relaxed text-foreground">
        {aiStatus === "thinking" ? (
          <div className="flex items-center gap-2 py-2">
            <span className="text-xs text-muted-foreground animate-pulse">Thinking&hellip;</span>
          </div>
        ) : (
          <>
            <div className="prose prose-sm max-w-none dark:prose-invert [&_a]:break-words [&_pre]:whitespace-pre-wrap [&_pre]:break-words [&_hr]:my-2">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  table: ({ node, ...props }) => (
                    <div className="not-prose overflow-x-auto my-1.5 rounded-lg border border-border bg-card">
                      <table className="w-full text-[13px] text-left border-collapse m-0" {...props} />
                    </div>
                  ),
                  thead: ({ node, ...props }) => <thead className="bg-muted/50 text-muted-foreground" {...props} />,
                  th: ({ node, ...props }) => (
                    <th className="px-2.5 py-1.5 font-medium border-b border-border" {...props} />
                  ),
                  td: ({ node, ...props }) => (
                    <td className="px-2.5 py-1.5 border-b border-border/50 last:border-0" {...props} />
                  ),
                  a: ({ node, ...props }) => (
                    <a
                      {...props}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline inline-flex items-center gap-1 font-medium"
                    >
                      {props.children}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ),
                  p: ({ node, children, ...props }) => (
                    <p className="mb-1 last:mb-0 leading-[1.5] text-[14px] whitespace-pre-wrap" {...props}>
                      {children}
                    </p>
                  ),
                  ul: ({ node, children, ...props }) => (
                    <ul className="mb-1 last:mb-0 ml-3 list-disc text-[14px] leading-[1.5]" {...props}>
                      {children}
                    </ul>
                  ),
                  ol: ({ node, children, ...props }) => (
                    <ol className="mb-1 last:mb-0 ml-3 list-decimal text-[14px] leading-[1.5]" {...props}>
                      {children}
                    </ol>
                  ),
                  li: ({ node, children, ...props }) => (
                    <li className="mb-0" {...props}>
                      {children}
                    </li>
                  ),
                  code: ({ node, className, children, ...props }) => {
                    const isInline = !className;
                    if (isInline) {
                      return (
                        <code
                          className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono"
                          {...props}
                        >
                          {children}
                        </code>
                      );
                    }
                    return (
                      <code
                        className="block bg-muted p-2 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap break-words"
                        {...props}
                      >
                        {children}
                      </code>
                    );
                  },
                  pre: ({ node, children, ...props }) => (
                    <pre
                      className="bg-muted p-2 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap break-words mb-1.5 last:mb-0"
                      {...props}
                    >
                      {children}
                    </pre>
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
            <div className="flex items-center gap-1.5 mt-1 justify-start">
              <button onClick={handleCopy} className={iconBtnClass} title="Copy message">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
              <span className="text-xs text-muted-foreground/50">
                {formatTime(message.timestamp)}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
